"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { rijderHref } from "@/lib/secties";
import type { StartlijstSectie, DeelnemerRij, DeelnemerSoort } from "@/server/wedstrijden";

const SOORT_LABEL: Record<DeelnemerSoort, string> = {
  competitie: "Competitie",
  eurocup: "Euro Cup",
  overig: "Overig",
};
const SOORT_VOLGORDE: DeelnemerSoort[] = ["competitie", "eurocup", "overig"];

function soortenIn(sectie: StartlijstSectie): DeelnemerSoort[] {
  return SOORT_VOLGORDE.filter((s) => sectie.klassen.some((k) => k.deelnemers.some((d) => d.soort === s)));
}

export function WedstrijdKlassen({ secties }: { secties: StartlijstSectie[] }) {
  const [sectieIdx, setSectieIdx] = useState(0);
  const sectie = secties[sectieIdx] ?? secties[0];
  const soorten = sectie ? soortenIn(sectie) : [];
  const [soort, setSoort] = useState<DeelnemerSoort>(soorten[0] ?? "competitie");
  // Alles dichtgeklapt beginnen: een open klasse met tientallen rijders duwt de
  // volgende blokken ver onder de vouw, waardoor het lijkt alsof die niet opengaan.
  const [openKlasse, setOpenKlasse] = useState<string | null>(null);

  if (!sectie) return null;

  const zichtbaar = sectie.klassen
    .map((k) => ({ ...k, deelnemers: k.deelnemers.filter((d) => d.soort === soort) }))
    .filter((k) => k.deelnemers.length > 0);

  function kiesSectie(i: number) {
    const s = secties[i];
    const so = soortenIn(s);
    setSectieIdx(i);
    setSoort(so[0] ?? "competitie");
    setOpenKlasse(null);
  }
  function kiesSoort(s: DeelnemerSoort) {
    setSoort(s);
    setOpenKlasse(null);
  }

  return (
    <div className="py-5">
      {secties.length > 1 && (
        <div className="mb-4 flex gap-2 overflow-x-auto px-4 pb-1">
          {secties.map((s, i) => (
            <button
              key={s.code}
              type="button"
              onClick={() => kiesSectie(i)}
              className={cn(
                "shrink-0 rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors",
                i === sectieIdx ? "bg-primary text-primary-foreground" : "bg-card text-white/70 hover:text-white",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {soorten.length > 1 && (
        <div className="mb-4 flex gap-2 px-4">
          {soorten.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => kiesSoort(s)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ring-1 transition-colors",
                s === soort ? "text-primary ring-primary" : "text-white/50 ring-white/15 hover:text-white/80",
              )}
            >
              {SOORT_LABEL[s]}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-3 px-4">
        {zichtbaar.map((k) => {
          const open = openKlasse === k.key;
          return (
            <div key={k.key} className="overflow-hidden rounded-xl border border-border bg-card">
              <button
                type="button"
                onClick={() => setOpenKlasse(open ? null : k.key)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
              >
                <span className="min-w-0 truncate font-display text-base font-bold uppercase tracking-wide text-white">
                  {k.naam}
                </span>
                <span className="flex shrink-0 items-center gap-2.5">
                  <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-bold tabular-nums text-primary">
                    {k.deelnemers.length}
                  </span>
                  <ChevronDown
                    className={cn(
                      "size-5 text-primary transition-transform duration-200",
                      open && "rotate-180",
                    )}
                    aria-hidden
                  />
                </span>
              </button>

              {open && (
                <ul className="divide-y divide-white/5 border-t border-white/5">
                  {k.deelnemers.map((d, i) => (
                    <li key={i}>
                      <DeelnemerItem d={d} href={rijderHref(sectie.code, d.deelnID)} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DeelnemerItem({ d, href }: { d: DeelnemerRij; href: string | null }) {
  const inhoud = (
    <>
      <span className="w-7 shrink-0 text-center font-display text-sm font-black tabular-nums text-primary">
        {d.startNR ?? "–"}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-white">{d.titel}</p>
        <p className="truncate text-xs text-muted-foreground">
          {[d.ondertitel, d.woonplaats].filter(Boolean).join(" · ") || " "}
        </p>
      </div>
    </>
  );

  if (!href) {
    return <div className="flex items-center gap-3 px-4 py-2.5">{inhoud}</div>;
  }
  return (
    <Link href={href} className="flex items-center gap-3 px-4 py-2.5 transition-colors active:bg-white/5">
      {inhoud}
      <ChevronRight className="size-4 shrink-0 text-white/30" aria-hidden />
    </Link>
  );
}
